import React, { ReactElement, useState } from 'react';
import { useMutation } from '@apollo/client';
import {
  MutationUploadSingleFileArgs,
  AdditionalFieldValue,
  useUpdateJobsiteWorkerMutation,
  useCreateJobsiteWorkerDocumentMutation,
  useCreateJobsiteWorkerDocumentVersionMutation,
  OnboardingStepKey,
  OnboardingStepCompleteErrorCode,
  OnboardingDocumentIncompleteStatus,
} from 'apollo/generated/client-operations';
import { InputFile } from 'types';
import { AuthContext } from 'auth';
import {
  getCurrentISOFormattedDateTime,
  getISOFormattedDate,
} from 'utils/dates';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import {
  getCurrentISOFormattedDate,
  isServerFile,
  uploadCompressedFile,
  useDeferredFormSubmission,
  useIsMounted,
} from 'utils';
import { DocumentKey } from 'containers/worker/utils';
import { AlertService } from 'components/alertService';
import {
  Form,
  FormOnSubmit,
  getUpdateInputValueFunction,
} from 'components/form';
import { Header } from 'components/header';
import { FILE_UPLOAD } from 'containers/workerOnboarding/helpers/queries';
import { JobsiteWorkerOnboardingFooterNavbar } from 'containers/workerOnboarding/navbar/JobsiteWorkerOnboardingFooterNavbar';
import {
  SiteSafetyTrainingDataType,
  OnboardingStepProps,
  UploadFileResponse,
  OnboardingStepCompleteErrorExtensions,
} from 'containers/workerOnboarding/types';
import { StepLoading } from './StepLoading';
import {
  getDefaultValues,
  getFormInputsHook,
  getMultiInstanceDocTypesByWorkerDocType,
} from './SiteSpecificTrainingStep.forms';
import { commonClasses } from './common.style';

const documentConfigKeys = [
  DocumentKey.SiteSpecificOrientation,
  DocumentKey.JobsiteSafetyVideo,
  DocumentKey.JobsiteSafetyDocument,
  DocumentKey.WorkerConsentDocument,
];

export function SiteSpecificTrainingStep(
  props: OnboardingStepProps,
): ReactElement {
  const {
    loading: parentLoading,
    jobsiteWorkerId,
    jobsiteWorker,
    jobsiteWorkerDocumentTypes,
    defaultFormValues,
    filteredDocumentTypeIds,
    documents,
    refetchJobsiteWorkerDocuments,
  } = props;

  const { currentUser: user } = React.useContext(AuthContext);
  const isContractorMember = user.isContractor;

  const isMounted = useIsMounted();
  const [fetching, setFetching] = useState<boolean>(false);
  const [isFormDirty, setIsFormDirty] = React.useState(false);
  const { formRef, deferredSubmission, submitForm } =
    useDeferredFormSubmission<SiteSafetyTrainingDataType>();

  const [updateJobsiteWorker] = useUpdateJobsiteWorkerMutation({
    onCompleted: () => {
      if (isMounted()) setFetching(false);
      refetchJobsiteWorkerDocuments();
      AlertService.alert('success', 'Successfully updated', null);
    },
    // error is handled imperatively when `updateJobsiteWorker` is called
    // onError: (error) => {
    //   setFetching(false);
    //   AlertService.alert(
    //     'danger',
    //     'Something went wrong!',
    //     getGraphQLError(error),
    //   );
    //   deferredSubmission.reject(error);
    // },
    refetchQueries: ['GetJobsiteWorker'],
  });

  const [uploadFile] = useMutation<
    UploadFileResponse,
    MutationUploadSingleFileArgs
  >(FILE_UPLOAD, {
    onError: (error) => {
      setFetching(false);
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
      deferredSubmission.reject(error);
    },
  });

  const [createJobsiteWorkerDocument] = useCreateJobsiteWorkerDocumentMutation({
    onError: (error) => {
      setFetching(false);
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
      deferredSubmission.reject(error);
    },
  });

  const [createJobsiteWorkerDocumentVersion] =
    useCreateJobsiteWorkerDocumentVersionMutation();

  const { documentKeys, multiInstanceDocTypesByWorkerDocType } =
    React.useMemo(() => {
      const multiInstanceDocTypesMap = getMultiInstanceDocTypesByWorkerDocType(
        jobsiteWorkerDocumentTypes,
      );
      return {
        multiInstanceDocTypesByWorkerDocType: multiInstanceDocTypesMap,
        documentKeys: documentConfigKeys.flatMap(
          (workerDocTypeKey): string[] | DocumentKey => {
            const multiInstanceDocTypes =
              multiInstanceDocTypesMap[workerDocTypeKey];
            if (multiInstanceDocTypes) {
              return multiInstanceDocTypes.map((dt) => dt.key);
            }
            return workerDocTypeKey;
          },
        ),
      };
    }, [jobsiteWorkerDocumentTypes]);

  const onSubmit: FormOnSubmit<SiteSafetyTrainingDataType> = async (
    data,
    _event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (fetching) return;
    setFetching(true);

    try {
      const dirtyFieldsKeys = Object.keys(dirtyFields);

      await Promise.all(
        documentKeys.map(async (documentKey: string) => {
          const isDirty = dirtyFieldsKeys.some((val) =>
            val.includes(documentKey),
          );
          if (!isDirty) return;

          const additionalFieldValues: (AdditionalFieldValue & {
            type: string;
          })[] = [];

          additionalFieldValues.push({
            key: 'orientation-date',
            value: getCurrentISOFormattedDate(),
            type: 'string',
          });

          const acknowledgmentProgressFractionRaw = data[
            `${documentKey}-acknowledgment-progress-fraction`
          ] as string;
          if (acknowledgmentProgressFractionRaw) {
            additionalFieldValues.push({
              key: 'acknowledgment-progress-fraction',
              value: acknowledgmentProgressFractionRaw,
              type: 'string',
            });
          }

          const acknowledgmentStatus =
            data[`${documentKey}-acknowledgment-status`] ?? false;
          if (acknowledgmentStatus) {
            additionalFieldValues.push({
              key: 'acknowledgment-status',
              value: 'confirmed',
              type: 'string',
            });
          }

          const acknowledgmentDateRaw = data[
            `${documentKey}-acknowledgment-date`
          ] as string;
          if (acknowledgmentDateRaw) {
            const acknowledgmentDate = getISOFormattedDate(
              acknowledgmentDateRaw,
            );
            additionalFieldValues.push({
              key: 'acknowledgment-date',
              value: acknowledgmentDate,
              type: 'date',
            });
          }

          const acknowledgedByUserId = data[
            `${documentKey}-acknowledged-by-user-id`
          ] as string;
          if (acknowledgedByUserId) {
            additionalFieldValues.push({
              key: 'acknowledged-by-user-id',
              value: acknowledgedByUserId,
              type: 'string',
            });
          }

          const files = [
            data[`${documentKey}-front`],
            data[`${documentKey}-back`],
          ]
            .flat()
            .filter(Boolean) as InputFile[];

          const fileIds = files?.length
            ? await Promise.all(
                files.map(async (file): Promise<string> => {
                  if (isServerFile(file)) {
                    return file.fileId;
                  }
                  return uploadCompressedFile(file, uploadFile);
                }),
              )
            : [];

          const jobsiteWorkerDocumentId = documents.find(
            ({ key }) => key === documentKey,
          )?.id;

          if (jobsiteWorkerDocumentId) {
            await createJobsiteWorkerDocumentVersion({
              variables: {
                jobsiteWorkerDocumentId,
                jobsiteWorkerDocumentVersionInput: {
                  fileIds,
                  additionalFieldValues,
                },
              },
            });
          } else {
            await createJobsiteWorkerDocument({
              variables: {
                jobsiteWorkerDocumentInput: {
                  jobsiteWorkerId,
                  jobsiteWorkerDocumentTypeId:
                    filteredDocumentTypeIds[documentKey],
                  key: documentKey,
                },
                jobsiteWorkerDocumentVersionInput: {
                  fileIds,
                  additionalFieldValues,
                },
              },
            });
          }
        }),
      );

      const getUpdateInputValue = getUpdateInputValueFunction(
        data,
        dirtyFields,
      );

      await updateJobsiteWorker({
        variables: {
          jobsiteWorkerId,
          jobsiteWorkerInput: {
            stickerNumber: getUpdateInputValue('stickerNumber'),
            stepToComplete: OnboardingStepKey.SiteSpecificOrientation,
            siteSpecificTrainingCompletedAt: getCurrentISOFormattedDateTime(),
            siteSpecificTrainingSkipReason: null,
          },
        },
      });

      deferredSubmission.resolve(true);
    } catch (error) {
      if (isMounted()) setFetching(false);
      const onboardingStepCompleteError =
        getGraphQLErrorByCode<OnboardingStepCompleteErrorExtensions>(
          error,
          OnboardingStepCompleteErrorCode.IncompleteRequiredVideos,
        );
      if (onboardingStepCompleteError) {
        const formValues = formApi.getValues();
        onboardingStepCompleteError.extensions.incompleteOnboardingVideos?.forEach(
          ({ key, status }) => {
            const statusFieldKey = `${key}-acknowledgment-status`;
            if (statusFieldKey in formValues) {
              formApi.setError(statusFieldKey, {
                message:
                  status === OnboardingDocumentIncompleteStatus.Expired
                    ? 'Onboarding video acknowledgment is expired.'
                    : 'Onboarding video is required but incomplete.',
                shouldFocus: true,
              });
            } else {
              const docTypeName =
                jobsiteWorkerDocumentTypes.find((dt) => dt.key === key)?.name ??
                key;
              AlertService.alert(
                'danger',
                'Site Specific Orientation Error!',
                status === OnboardingDocumentIncompleteStatus.Expired
                  ? `"${docTypeName}" onboarding video acknowledgment is expired.`
                  : `"${docTypeName}" onboarding video is required but incomplete.`,
              );
            }
          },
        );
        deferredSubmission.resolve(false);
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
        deferredSubmission.reject(error);
      }
    }
  };

  const overrideComplete = (reason: string): void => {
    if (fetching) return;
    setFetching(true);

    updateJobsiteWorker({
      variables: {
        jobsiteWorkerId,
        jobsiteWorkerInput: {
          siteSpecificTrainingCompletedAt: getCurrentISOFormattedDateTime(),
          siteSpecificTrainingSkipReason: reason,
        },
      },
    });
  };

  const resetComplete = (): void => {
    if (fetching) return;
    setFetching(true);

    updateJobsiteWorker({
      variables: {
        jobsiteWorkerId,
        jobsiteWorkerInput: {
          siteSpecificTrainingCompletedAt: null,
          siteSpecificTrainingSkipReason: null,
        },
      },
    });
  };

  const loading = parentLoading || fetching;

  const inputs = React.useMemo(
    () =>
      getFormInputsHook({
        loading,
        jobsiteWorker,
        defaultFormValues,
        filteredDocumentTypeIds,
        jobsiteWorkerDocumentTypes,
        documents,
      }),
    [
      loading,
      jobsiteWorker,
      defaultFormValues,
      filteredDocumentTypeIds,
      jobsiteWorkerDocumentTypes,
      documents,
    ],
  );

  const defaultValues = React.useMemo(
    () =>
      getDefaultValues(
        jobsiteWorker,
        multiInstanceDocTypesByWorkerDocType,
        defaultFormValues,
      ),
    [defaultFormValues, multiInstanceDocTypesByWorkerDocType, jobsiteWorker],
  );

  return (
    <>
      <div className="odin-relative">
        <fieldset disabled={!!isContractorMember}>
          <Header title="Site specific orientation" />
          <StepLoading loading={loading} />
          <Form
            inputs={inputs}
            ref={formRef}
            defaultValues={defaultValues}
            onIsDirtyChange={setIsFormDirty}
            onSubmit={onSubmit}
            className={commonClasses.form}
            inputsContainerClassName={commonClasses.formInputsContainer}
          />
        </fieldset>
      </div>
      <JobsiteWorkerOnboardingFooterNavbar
        jobsiteWorker={jobsiteWorker}
        isFormDirty={isFormDirty}
        onSave={submitForm}
        onResetComplete={resetComplete}
        onForceComplete={overrideComplete}
        showExemptionReason
        hideLockSession
        hideSave={isContractorMember}
        hideSkipOptions={isContractorMember}
      />
    </>
  );
}
